import React from "react";
import { Card, Form, Alert } from "react-bootstrap";
import { WithTranslation } from "react-i18next";
import { Event, EventGuestStatistic } from "../../../../models/Event";
import { OutputSettings } from "../useCreateWizard";
import { BaseValidatedFormGroup, ValidatedSelect } from "../validation";

export interface OutputSettingsBaseCardProps {
    outputSettings: OutputSettings;
    onOutputSettingsChange: (settings: Partial<OutputSettings>) => void;
    selectedEvent: Event | null;
    eventGuestStatistic: EventGuestStatistic | null;
}

export abstract class OutputSettingsBaseCard<
    P extends OutputSettingsBaseCardProps & WithTranslation = OutputSettingsBaseCardProps & WithTranslation,
> extends React.Component<P> {
    protected handleSettingChange = (key: keyof OutputSettings, value: any) => {
        this.props.onOutputSettingsChange({
            [key]: value,
        });
    };

    /**
     * Check if write mode selector should be shown based on guest statistics.
     * Shows only if the selected field has existing data for the relevant guest filter.
     */
    protected shouldShowWriteModeSelector(selectedFieldId: string | null): boolean {
        const { eventGuestStatistic, outputSettings } = this.props;
        
        if (!eventGuestStatistic || !selectedFieldId) {
            return false;
        }

        // Get the appropriate statistic based on guest filter selection
        let relevantStatistic;
        switch (outputSettings.guestFilter) {
            case "CHECKED_IN":
                relevantStatistic = eventGuestStatistic.attended;
                break;
            case "ACCEPTED":
                relevantStatistic = eventGuestStatistic.accepted;
                break;
            case "ALL":
            default:
                relevantStatistic = eventGuestStatistic.all;
                break;
        }

        // Check if any guests have filled this field
        const filledCount = relevantStatistic.filledFields[selectedFieldId] || 0;
        return filledCount > 0;
    };

    protected abstract getTitleTranslationKey(): string;
    protected abstract renderCardBody(): React.ReactNode;

    protected renderCustomFieldSelector(config: {
        texts: {
            label: string;
            description: string;
            noFieldsMessage: string;
        };
        customFields: any[];
        selectedFieldId: string | null;
        onFieldIdChange: (value: string) => void;
        writeMode?: {
            value: boolean;
            onChange: (value: boolean) => void;
        };
        validation?: any;
    }): React.ReactNode {
        const { texts } = config;
        const { t } = this.props;

        return (
            <>
                <BaseValidatedFormGroup className="mb-3" validation={config.validation}>
                    <Form.Label>{texts.label}</Form.Label>
                    {config.customFields.length === 0 ? (
                        <Alert variant="warning">{texts.noFieldsMessage}</Alert>
                    ) : (
                        <ValidatedSelect
                            value={config.selectedFieldId || ""}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                config.onFieldIdChange(e.target.value)
                            }
                            validation={config.validation}
                        >
                            <option key="__empty__" value="">
                                {t("cards.common.selectCustomField")}
                            </option>
                            {config.customFields.map((fieldDef) => (
                                <option key={fieldDef.id} value={fieldDef.id}>
                                    {fieldDef.name}
                                    {fieldDef.groupName ? ` (${fieldDef.groupName})` : ""}
                                </option>
                            ))}
                        </ValidatedSelect>
                    )}
                    <Form.Text className="text-muted">{texts.description}</Form.Text>
                </BaseValidatedFormGroup>
                {config.writeMode && config.customFields.length > 0 && this.shouldShowWriteModeSelector(config.selectedFieldId) && (
                    <Form.Group className="mb-3">
                        <Form.Label className="form-label-overline">{t("cards.storage.writeModeLabel")}</Form.Label>
                        <Form.Check
                            type="radio"
                            id="write-mode-skip"
                            name="writeMode"
                            label={t("cards.storage.writeModeSkip")}
                            checked={!config.writeMode.value}
                            onChange={() => config.writeMode!.onChange(false)}
                            className="mb-1"
                        />
                        <Form.Check
                            type="radio"
                            id="write-mode-overwrite"
                            name="writeMode"
                            label={t("cards.storage.writeModeOverwrite")}
                            checked={config.writeMode.value}
                            onChange={() => config.writeMode!.onChange(true)}
                        />
                        <Form.Text className="text-muted">{t("cards.storage.writeModeDescription")}</Form.Text>
                    </Form.Group>
                )}
            </>
        );
    }

    render() {
        const { t } = this.props;

        return (
            <Card className="mb-3">
                <Card.Header>
                    <h5 className="mb-0">{t(this.getTitleTranslationKey())}</h5>
                </Card.Header>
                <Card.Body>{this.renderCardBody()}</Card.Body>
            </Card>
        );
    }
}
